import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import Modal from 'src/components/modal.jsx';
import Input from 'src/components/ui/Input';
import Radio from 'src/components/ui/radio';
import { pluralWord } from 'src/utils/stringUtils';

const TIME_FORMAT = 'dddd MMM D, HH:mm';

const CompleteWithActualsModal = ({
  onSubmit,
  onClose,
  runEstimates,
  loadRunActuals,
}) => {
  const now = dayjs();
  const { addToast } = useToasts();

  const [submitted, setSubmitted] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [runActuals, setRunActuals] = useState(null);
  const [activeOption, setActiveOption] = useState('scheduled');
  const [manualDate, setManualDate] = useState(now.format('YYYY-MM-DD'));
  const [manualTime, setManualTime] = useState('');

  const estimatedEnd = dayjs(runEstimates[0]?.estimates?.end);
  const actualStartTime = dayjs(runActuals?.start_in_progress_time);

  const scheduledEndTime = estimatedEnd.format(TIME_FORMAT);
  const currentTime = now.format(TIME_FORMAT);

  const elapsedMinutes = now.diff(estimatedEnd, 'minute');
  const elapsedHours = Math.round(elapsedMinutes / 60);

  const elapsedText = elapsedMinutes < 60
    ? `${elapsedMinutes} ${pluralWord('minute', elapsedMinutes)}`
    : `${elapsedHours} ${pluralWord('hour', elapsedHours)}`;

  const manualEndDateTime =
    manualDate && manualTime
        ? dayjs(`${manualDate}T${manualTime}`)
        : null;

  const endDateBeforeStart =
    actualStartTime.isValid() &&
    dayjs(manualDate).isBefore(actualStartTime, 'day');

  const endTimeBeforeStart =
    actualStartTime.isValid() &&
    manualEndDateTime?.isBefore(actualStartTime);

  const dateError = !manualDate
    ? 'End date is required.'
    : endDateBeforeStart
      ? `End date must be on or after the run start date (${actualStartTime.format('MMM D')}).`
      : null;

  const timeError = !manualTime
    ? 'End time is required.'
    : endTimeBeforeStart
      ? `End time must be after the run start time (${actualStartTime.format('HH:mm')}).`
      : null;

  const handleManualOptionSelect = async () => {
    const actuals = await loadRunActuals();
    setRunActuals(actuals?.[0] || null);
    setSubmitted(false);
    setActiveOption('manual');
  };

  const hideModal = () => {
    setSaveError(null);
    setSubmitted(false);
    onClose();
  };

  const onModalDismiss = () => {
    hideModal();
  };

  const onModalConfirm = async () => {
    setSubmitted(true);
    if (activeOption === 'manual' && (dateError || timeError)) return;

    try {
      let selectedEndTime;

      switch (activeOption) {
        case 'scheduled':
          selectedEndTime = estimatedEnd.toISOString();
          break;
        case 'current':
          selectedEndTime = dayjs().toISOString();
          break;
        case 'manual':
          if (!manualEndDateTime) return;
          selectedEndTime = manualEndDateTime.toISOString();
          break;
        default:
          return;
      }

      await onSubmit(selectedEndTime);
      hideModal();
    } catch (error) {
      setSaveError(error);
    }
  };

  useEffect(() => {
    if (saveError) {
      addToast('There was an error completing run. Please try again.', { appearance: 'error' });
    }
  }, [saveError]);

  return (
    saveError ? null : (
      <Modal
        isOpen
        title="Complete Run"
        dismissText="Cancel"
        confirmText="Complete"
        onConfirm={onModalConfirm}
        onDismiss={onModalDismiss}
      >
        <p>
          Your run is finishing {elapsedText} after its scheduled completion.
          Would you like to adjust the end time as you complete the run?
        </p>
        <div className="form-group">
          <Radio
            id="schedule-end-time-option"
            name="schedule-end-time"
            className="radio--stacked"
            checked={activeOption === 'scheduled'}
            label={
              <div className="radio__label">
                Use Scheduled End time
                <span className="radio__sub-label">
                  {scheduledEndTime}
                </span>
              </div>
            }
            onChange={() => setActiveOption('scheduled')}
          />
        </div>
        <div className="form-group">
          <Radio
            id="current-time-option"
            name="schedule-end-time"
            className="radio--stacked"
            checked={activeOption === 'current'}
            label={
              <div className="radio__label">
                Use Current Time
                <span className="radio__sub-label">
                  {currentTime}
                </span>
              </div>
            }
            onChange={() => setActiveOption('current')}
          />
        </div>
        <div className="form-group mb-2">
          <Radio
            id="manual-time-option"
            name="schedule-end-time"
            checked={activeOption === 'manual'}
            label="Enter Exact Time"
            onChange={handleManualOptionSelect}
          />
          {activeOption === 'manual' && (
            <div className='d-flex flex-column gap-10 mt-3'>
              <div>
                <Input
                  type='date'
                  value={manualDate}
                  onChange={event => setManualDate(event.target.value)}
                />
                {submitted && dateError && (
                  <p className="mt5 mb0 small text-danger">
                    {dateError}
                  </p>
                )}
              </div>
              <div>
                <Input
                  type='time'
                  value={manualTime}
                  onChange={event => setManualTime(event.target.value)}
                />
                {submitted && timeError && (
                  <p className="mt5 mb0 small text-danger">
                    {timeError}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    )
  );
};

CompleteWithActualsModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loadRunActuals: PropTypes.func.isRequired,
  runEstimates: PropTypes.arrayOf(
    PropTypes.shape({
      estimates: PropTypes.shape({ end: PropTypes.string }),
    }),
  ).isRequired,
};

export default CompleteWithActualsModal;
